import { UTM_CAMPAIGN_PARAM } from '~/domain/OfficeNotification';
import { ConfirmCandidateSource, type TConfirmCandidateSource } from './types';

/**
 * Reading a confirmation-outreach landing (GOTU-4392) — the Branch link that lands
 * straight on the confirm route, rather than navigating to it from inside the portal.
 *
 * One minted link serves every channel: the same `confirmDeepLink` stored on the
 * counter at application time is reused by the email, the text and the digest. So the
 * campaign identifies the *link* and the medium identifies the *channel*, and the two
 * questions are answered by different params — see below.
 */

/**
 * Query param carrying which channel delivered a link.
 *
 * `utm_campaign` is imported from the office-notification contract rather than
 * redeclared: the two contracts genuinely share the one param now, and one constant
 * keeps that visible instead of letting two copies drift apart.
 */
export const UTM_MEDIUM_PARAM = 'utm_medium';

/**
 * `utm_campaign` set when the BE mints the confirmation-outreach link
 * (`branchDefaultBaseArgs.confirmProfessional`). Every channel carries it, which is
 * exactly why it identifies the link.
 */
export const CONFIRM_OUTREACH_UTM_CAMPAIGN = 'confirm_professional';

/**
 * `utm_medium` these links carried before the campaign label shipped, forwarded by
 * Branch from the link's `$feature`.
 *
 * Kept only to recognise links minted before GOTU-5966: those carry no campaign, and
 * a shift they point at can still be awaiting a decision. It is deliberately *not*
 * the primary key — the text sender replaces this value with `sms` rather than
 * appending to it, which is what broke the original `utm_medium`-only check.
 */
export const LEGACY_CONFIRM_OUTREACH_UTM_MEDIUM = 'confirm-professional';

/** `utm_medium` the BE appends when it sends the confirm link by text (GOTU-5966). */
const SMS_UTM_MEDIUM = 'sms';

/** Which kind of message the confirmation-outreach link came from (GOTU-6492). */
export const ConfirmOutreachChannel = {
  Email: 'email',
  Sms: 'sms',
} as const;

export type TConfirmOutreachChannel =
  (typeof ConfirmOutreachChannel)[keyof typeof ConfirmOutreachChannel];

export interface IConfirmOutreachSourceArgs {
  /** Channel the link came from. Omitted means email. */
  channel?: TConfirmOutreachChannel;
  /** `pending` or `counter` — decides the applied/counter split. */
  status?: string;
}

/**
 * Whether this landing came from a confirmation-outreach link.
 *
 * Keyed on the campaign, because that is set once at mint and is therefore on every
 * variant. Keying on `utm_medium` is what GOTU-6492 fixed: the text sender *replaces*
 * the medium with `sms`, so the old exact match stopped recognising text arrivals and
 * would fail again for the next channel added.
 *
 * No collision with the office-notification contract (GOTU-4135), which shares
 * `utm_campaign`: `confirm_professional` is not one of its notification keys, and
 * those links additionally require `utm_source=email`, which these never carry.
 */
export function isConfirmOutreachLanding(searchParams: URLSearchParams): boolean {
  return (
    searchParams.get(UTM_CAMPAIGN_PARAM) === CONFIRM_OUTREACH_UTM_CAMPAIGN ||
    searchParams.get(UTM_MEDIUM_PARAM) === LEGACY_CONFIRM_OUTREACH_UTM_MEDIUM
  );
}

/**
 * Reads which channel a confirmation-outreach landing came from, or `undefined` when
 * the link carries no channel marker.
 *
 * Matched exactly against `sms`, never merely "present": on an untagged link the
 * medium is `confirm-professional`, which is a Branch feature label and not a channel
 * at all. Nothing would error — a report grouped by channel would just show a tidy
 * bucket quietly containing arrivals of unknown origin.
 *
 * `undefined` rather than email so the caller decides the fallback; see
 * {@link confirmOutreachSource}.
 */
export function confirmOutreachChannelFromLanding(
  searchParams: URLSearchParams,
): TConfirmOutreachChannel | undefined {
  return searchParams.get(UTM_MEDIUM_PARAM) === SMS_UTM_MEDIUM
    ? ConfirmOutreachChannel.Sms
    : undefined;
}

/**
 * Picks the confirm source for a confirmation-outreach landing from the channel and
 * the shift's status.
 *
 * The plain-versus-counter split can't come from the link — every variant shares the
 * one minted link and they differ only in which shift they point at — so `counter` vs
 * `pending` remains the only signal. It is the distinction the rest of the app already
 * branches on for this pair of states (the `/dashboard/job/:jobId` redirect, and the
 * mobile Schedule's `getScreenNameByStatus`), and it mirrors how the
 * `office_notification` events split offer-received from counter-offer-received.
 *
 * Any other status returns `undefined`: the shift is no longer awaiting a confirm
 * decision, so attributing the confirm to the outreach message would be guessing.
 *
 * A missing channel falls back to email, which is where every link minted before
 * GOTU-5966 came from. Kept identical to the mobile app's copy so one PostHog insight
 * spans both platforms.
 */
export function confirmOutreachSource({
  channel = ConfirmOutreachChannel.Email,
  status,
}: IConfirmOutreachSourceArgs): TConfirmCandidateSource | undefined {
  const isSms = channel === ConfirmOutreachChannel.Sms;

  if (status === 'counter') {
    return isSms
      ? ConfirmCandidateSource.SmsCandidateAppliedCounter
      : ConfirmCandidateSource.EmailCandidateAppliedCounter;
  }

  if (status === 'pending') {
    return isSms
      ? ConfirmCandidateSource.SmsCandidateApplied
      : ConfirmCandidateSource.EmailCandidateApplied;
  }

  return undefined;
}
